import moment from 'moment';

import {
  Invoice,
  InvoicePatch,
  InvoiceQueryFilters,
  InvoiceRow,
  InvoiceSortColumns,
  DocumentType,
  PaymentStatus,
  PaymentChannel
} from 'openapi';

import { AdditionalInvoiceFields } from 'components/forms/InvoiceVerificationForms/ReceiverForm';

import { DATE_FORMATS, INITIAL_IBAN_ITEM } from 'utils/constants/constants';
import { INITIAL_PRODUCT_ITEM } from 'utils/constants/invoices';
import { getConfidenceSnapshot } from 'utils/helpers/confidenceHelpers';
import { decimalFormater } from 'utils/helpers/invoiceHelpers';
import {
  InvoicesFiltersForm,
  SendForApprovalFormValues
} from 'utils/interfaces/InvoiceProps';

export const mapInvoice = (invoice: Invoice): Invoice =>
  ({
    counterpartyName: invoice?.counterpartyName || '',
    invoiceNumber: invoice?.invoiceNumber || '',
    invoiceDate: invoice?.invoiceDate || '',
    counterpartyRegistrationNumber:
      invoice?.counterpartyRegistrationNumber || '',
    dueDate: invoice?.dueDate || '',
    counterpartyVatNumber: invoice?.counterpartyVatNumber || '',
    vatBase: decimalFormater(invoice?.vatBase as number),
    vatAmount: decimalFormater(invoice?.vatAmount as number) || '',
    invoiceAmount: decimalFormater(invoice?.invoiceAmount as number) || '',
    currencyId: invoice?.currencyId || 1,
    currency: invoice?.currency || 1,
    documentDescription: invoice?.documentDescription || '',
    products:
      invoice?.products && invoice.products.length > 0
        ? invoice.products
        : [{ ...INITIAL_PRODUCT_ITEM }],
    expenseType: invoice?.expenseType || null,
    documentType: invoice?.documentType || '',
    companyId: invoice?.companyId || '',
    counterpartyId: invoice?.counterpartyId || '',
    profitAndLossAccountId: invoice.profitAndLossAccountId || '',
    isCompanyVerified: invoice.isCompanyVerified || false,
    isCounterpartyVerified: invoice.isCounterpartyVerified || false,
    isDocumentDataVerified: invoice.isDocumentDataVerified || false,
    areProductsVerified: invoice.areProductsVerified || false,
    ibans: invoice.ibans?.length ? invoice.ibans : [{ ...INITIAL_IBAN_ITEM }]
  }) as Invoice;

export const mapInvoiceForReceiverSubmit = (
  invoice: InvoicePatch & AdditionalInvoiceFields
): InvoicePatch => ({
  companyId: invoice?.companyId,
  documentType: invoice?.documentType,
  isPrepaid: invoice?.isPrepaid,
  paymentChannel: invoice?.isPrepaid ? invoice?.paymentChannel : undefined,
  isReimbursement: invoice?.isReimbursement
});

export const mapInvoiceForSupplierSubmit = (
  invoice: InvoicePatch
): InvoicePatch => ({
  counterpartyId: invoice.isReimbursement ? undefined : invoice.counterpartyId,
  counterpartyName: invoice.counterpartyName,
  counterpartyVatNumber: invoice.isReimbursement
    ? undefined
    : invoice.counterpartyVatNumber || undefined,
  counterpartyRegistrationNumber: invoice.isReimbursement
    ? undefined
    : invoice.counterpartyRegistrationNumber || undefined,
  expenseTypeId: (invoice as Invoice).expenseType?.id,
  products: invoice.products,
  ibans: invoice.ibans
});
export const mapInvoiceForInvoiceSubmit = (
  invoice: InvoicePatch
): InvoicePatch => ({
  currencyId: invoice.currencyId,
  dueDate: invoice.dueDate,
  expenseTypeId: (invoice as Invoice).expenseType?.id || undefined,
  invoiceAmount: invoice.invoiceAmount,
  invoiceDate: invoice.invoiceDate,
  invoiceNumber: invoice.invoiceNumber,
  products: invoice.products,
  vatAmount: invoice.vatAmount,
  vatBase: invoice.vatBase,
  documentDescription: invoice.documentDescription,
  tags: invoice.tags
});

export const mapInvoiceForSummarySubmit = (
  invoice: InvoicePatch
): InvoicePatch => ({
  currencyId: invoice.currencyId,
  dueDate: invoice.dueDate,
  expenseTypeId: (invoice as Invoice).expenseType?.id,
  invoiceAmount: invoice.invoiceAmount,
  invoiceDate: invoice.invoiceDate,
  invoiceNumber: invoice.invoiceNumber,
  products: invoice.products,
  vatAmount: invoice.vatAmount,
  vatBase: invoice.vatBase,
  documentDescription: invoice.documentDescription,
  tags: invoice.tags,
  paymentChannel: invoice.paymentChannel,
  documentType: invoice.documentType,
  paymentStatus: invoice.paymentStatus
});

const formatAmount = (
  value: number | string | undefined
): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  return Number(value).toFixed(2);
};

export const mapInvoiceForSendForApprovalSubmit = (
  values: SendForApprovalFormValues,
  currentInvoice?: Invoice,
  dirtyFieldNames: string[] = []
): InvoicePatch => {
  const expenseTypeId =
    values.expenseType !== undefined ? Number(values.expenseType) : undefined;

  return {
    companyId: values.companyId,
    documentType: values.documentType,
    isPrepaid: values.isReimbursement ? false : values.isPrepaid,
    isReimbursement: values.isReimbursement,
    paymentChannel:
      values.isPrepaid && !values.isReimbursement
        ? values.paymentChannel
        : undefined,
    counterpartyId: values.counterpartyId || undefined,
    counterpartyName: values.counterpartyName,
    counterpartyRegistrationNumber: values.counterpartyRegistrationNumber,
    counterpartyVatNumber: values.counterpartyVatNumber,
    reimbursementCounterpartyId: values.isReimbursement
      ? values.reimbursementCounterpartyId
      : undefined,
    reimbursementIban:
      values.isReimbursement && values.reimbursementIban
        ? values.reimbursementIban
        : undefined,
    invoiceNumber: values.invoiceNumber,
    invoiceDate: values.invoiceDate,
    dueDate: values.dueDate,
    vatBase: formatAmount(values.vatBase) as unknown as number,
    vatAmount: formatAmount(values.vatAmount) as unknown as number,
    invoiceAmount: formatAmount(values.invoiceAmount) as unknown as number,
    expenseTypeId,
    currencyId:
      values.currencyId !== undefined ? Number(values.currencyId) : undefined,
    documentDescription: values.documentDescription,
    tags: values.tags,
    confidence: currentInvoice
      ? getConfidenceSnapshot(currentInvoice, dirtyFieldNames)
      : undefined
  };
};

export const getMappedInvoiceFilters = (
  filters: InvoicesFiltersForm
): InvoiceQueryFilters => ({
  stages: filters.stages,
  counterpartyNames: filters.counterpartyNames,
  shortCompanyNames: filters.shortCompanyNames,
  expenseTypes: filters.expenseTypes,
  insertedBy: filters.insertedBy,
  approvers: filters.approvers,
  invoiceDate: {
    from: filters.invoiceDateFrom
      ? moment(filters.invoiceDateFrom).format(DATE_FORMATS.displayedDateFormat)
      : undefined,
    to: filters.invoiceDateTo
      ? moment(filters.invoiceDateTo).format(DATE_FORMATS.displayedDateFormat)
      : undefined
  },
  uploadedDate: {
    from: filters.uploadedDateFrom
      ? moment(filters.uploadedDateFrom).format(
          DATE_FORMATS.displayedDateFormat
        )
      : undefined,
    to: filters.uploadedDateTo
      ? moment(filters.uploadedDateTo).format(DATE_FORMATS.displayedDateFormat)
      : undefined
  },
  amount: {
    from: filters.amountFrom || undefined,
    to: filters.amountTo || undefined
  },
  isBooked: filters.accounted ? filters.accounted === 'true' : undefined,
  documentTypes: filters.documentTypes
    ? (filters.documentTypes as DocumentType[])
    : undefined,
  invoiceNumber: filters.documentNumber || undefined,
  isReimbursement: filters.isReimbursement
    ? filters.isReimbursement === 'true'
    : undefined,
  tags: filters.tags,
  paymentStatuses: Array.isArray(filters.paymentStatuses)
    ? filters.paymentStatuses
    : undefined,
  paymentChannel: (filters.paymentChannel as PaymentChannel) || undefined
});

export const getMappedInvoices = (invoices: InvoiceRow[]) =>
  invoices.map((invoice: InvoiceRow) => ({
    id: invoice.id,
    companyId: invoice.companyId,
    [InvoiceSortColumns.STAGE]: invoice.stage,
    [InvoiceSortColumns.SHORT_COMPANY_NAME]: invoice.shortCompanyName,
    [InvoiceSortColumns.COUNTERPARTY_NAME]: invoice.counterpartyName,
    [InvoiceSortColumns.INVOICE_NUMBER]: invoice.invoiceNumber,
    [InvoiceSortColumns.INVOICE_DATE]: invoice.invoiceDate,
    [InvoiceSortColumns.INVOICE_AMOUNT]: invoice.invoiceAmount,
    [InvoiceSortColumns.INSERTED_BY]: invoice.insertedBy,
    [InvoiceSortColumns.INSERTED_AT]: invoice.insertedAt,
    currency: invoice.currency,
    [InvoiceSortColumns.EXPENSE_TYPE]: invoice.expenseType,
    isDeletable: invoice.isDeletable,
    stage: invoice.stage,
    isAccounted: invoice.isBooked,
    isPrepaid: invoice?.paymentStatus === PaymentStatus.PREPAID,
    documentType: invoice.documentType,
    isExportable: invoice.isExportable,
    isApprovable: invoice.isApprovable,
    isCounterpartyValidated: invoice.isCounterpartyValidated,
    isReimbursement: invoice.isReimbursement,
    isPayable: invoice.isPayable,
    paymentChannel: invoice.paymentChannel,
    paymentStatus: invoice.paymentStatus,
    gates: invoice.gates
  }));

export const getFormFilters = (filters: any): InvoicesFiltersForm => {
  return {
    stages: filters.stages || [],
    counterpartyNames: filters.counterpartyNames || [],
    shortCompanyNames: filters.shortCompanyNames || [],
    expenseTypes: filters.expenseTypes || [],
    insertedBy: filters.insertedBy || [],
    approvers: filters.approvers || [],
    invoiceDateFrom: filters.invoiceDate.from
      ? moment(
          filters.invoiceDate.from,
          DATE_FORMATS.displayedDateFormat
        ).toDate()
      : null,
    invoiceDateTo: filters.invoiceDate.to
      ? moment(
          filters.invoiceDate.to,
          DATE_FORMATS.displayedDateFormat
        ).toDate()
      : null,
    uploadedDateFrom: filters.uploadedDate.from
      ? moment(
          filters.uploadedDate.from,
          DATE_FORMATS.displayedDateFormat
        ).toDate()
      : null,
    uploadedDateTo: filters.uploadedDate.to
      ? moment(
          filters.uploadedDate.to,
          DATE_FORMATS.displayedDateFormat
        ).toDate()
      : null,
    amountFrom: filters.amount.from || '',
    amountTo: filters.amount.to || '',
    accounted:
      filters.isAccounted !== undefined && filters.isAccounted !== null
        ? filters.isAccounted.toString()
        : '',
    documentTypes: filters.documentTypes || [],
    documentNumber: filters.invoiceNumber || '',
    isReimbursement:
      filters.isReimbursement !== undefined && filters.isReimbursement !== null
        ? filters.isReimbursement.toString()
        : '',
    tags: filters.tags || [],
    paymentStatuses: filters.paymentStatuses || [],
    paymentChannel: filters.paymentChannel || ''
  };
};
